import React from 'react';
import {
    askPermission,
    getNotificationPermissionState,
    getSubscription,
    sendSubscriptionToBackEnd,
    subscribeUserToPush,
    supportSWPushNotification,
    unsubscribeUserFromPush,
} from '../../utils/notifications';
import { bugsnag, trackEvent } from '../../utils/platform';

interface State {
    browserNotificationsSupported: boolean;
    browserNotificationsEnabled?: boolean;
    browserNotificationsBlocked?: boolean;
    pending?: boolean;
}
type ChildProps = State & {
    handleEnableButtonClick(caller?: string): void;
};

interface Props {
    webPushVAPIDPublicKey: string | null;
    welcomeNotification: {
        title: string;
        body: string;
        clickURL?: string;
    };
    render(state: ChildProps): React.ReactNode;
}

class BrowserNotificationsHandler extends React.Component<Props, State> {
    readonly state = {
        browserNotificationsSupported: false,
        browserNotificationsEnabled: false,
        browserNotificationsBlocked: false,
        pending: false,
    };

    componentDidMount() {
        Promise.all([
            getNotificationPermissionState(),
            getSubscription(),
            supportSWPushNotification(),
        ]).then(
            results => {
                const permissionState = results[0];
                const subscription = results[1];
                const swNotificationSupport = results[2];
                this.setState({
                    browserNotificationsEnabled: permissionState === 'granted' && !!subscription,
                    browserNotificationsBlocked: permissionState === 'denied',
                    browserNotificationsSupported: !!swNotificationSupport,
                });
            },
            err => {
                if (window.bugsnag) {
                    window.bugsnag.notify(
                        'Failed to fetch notification subscription or permission state',
                        err
                    );
                }
            }
        );
    }
    handleEnableButtonClick = (caller?: string) => {
        const {
            browserNotificationsEnabled,
            browserNotificationsBlocked,
            browserNotificationsSupported,
        } = this.state;

        if (browserNotificationsBlocked || !browserNotificationsSupported) {
            return Promise.resolve();
        }
        this.setState({
            pending: true,
        });
        if (browserNotificationsEnabled) {
            trackEvent('Disable Browser Notifications Button Clicked');

            return unsubscribeUserFromPush()
                .then(() => {
                    this.setState({
                        browserNotificationsEnabled: false,
                        pending: false,
                    });
                })
                .catch(err => {
                    bugsnag.notify('Browser Disable error', err);
                    this.setState({ pending: false });
                });
        } else {
            trackEvent('Enable Browser Notifications Button Clicked', { called_by: caller });
            return askPermission()
                .then((permissionState: NotificationPermission) => {
                    switch (permissionState) {
                        case 'granted':
                            if (this.props.webPushVAPIDPublicKey == null) {
                                throw Error('webPushVAPIDPublicKey not defined');
                            }
                            return subscribeUserToPush(this.props.webPushVAPIDPublicKey);
                        case 'denied':
                            trackEvent('Browser Notifications Blocked');
                            this.setState({
                                browserNotificationsEnabled: false,
                                browserNotificationsBlocked: true,
                                pending: false,
                            });
                            return Promise.resolve(null);
                        default:
                            return Promise.resolve(null);
                    }
                })
                .then(subscription => {
                    return subscription
                        ? sendSubscriptionToBackEnd(subscription)
                        : Promise.resolve(null);
                })
                .then(response => {
                    if (response && response.ok) {
                        trackEvent('Browser Notifications Enabled');

                        this.setState({
                            browserNotificationsEnabled: true,
                            pending: false,
                        });
                        this.sendWelcomeNotification();
                    } else {
                        this.setState({ pending: false });
                    }
                })
                .catch(err => {
                    bugsnag.notify('Browser Enable error', err);
                    this.setState({ pending: false });
                });
        }
    };

    sendWelcomeNotification() {
        const { title, body, clickURL } = this.props.welcomeNotification;
        const notification = new Notification(title, { body });
        if (clickURL) {
            notification.onclick = function(e) {
                trackEvent('Welcome Notification Clicked');
                window.open(clickURL, '_blank');
            };
        }
    }

    render() {
        return this.props.render({
            ...this.state,
            handleEnableButtonClick: this.handleEnableButtonClick,
        });
    }
}

export default BrowserNotificationsHandler;
